import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Tooltip
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { AdminPageContainer } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import SkillDialog from '../../components/admin/SkillDialog';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import { getSkills, getSkillUsage, createSkill, editSkill, removeSkill } from '../../redux/slices/skill';
import { useDispatch, useSelector } from 'react-redux';

const EMPTY_USAGE = { openTasks: 0, openProjects: 0, openQuotes: 0, templates: 0, accountRates: 0, safeToDisable: true };

const SearchRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const SkillRow = styled(TableRow, { shouldForwardProp: (prop) => prop !== 'muted' })(({ muted }) => ({
    opacity: muted ? 0.55 : 1,
}));

const StatusCell = styled(TableCell)({
    whiteSpace: 'nowrap',
});

const UsageStack = styled(Box)({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
});

const UsageCounts = styled(Typography)(({ theme }) => ({
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
}));

const TooltipLines = styled(Box)({
    whiteSpace: 'pre-line',
});

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const usageSummary = (row) => {
    const parts = [plural(row.openTasks, 'open task'), plural(row.openProjects, 'open project')];
    if (row.openQuotes > 0) parts.push(plural(row.openQuotes, 'open quote'));
    if (row.templates > 0) parts.push(plural(row.templates, 'template'));
    return parts.join(' · ');
};

const usageTooltip = (row) => {
    const lines = [
        row.safeToDisable
            ? 'Nothing open still points at this skill, so disabling it takes nothing away from work in flight.'
            : 'Still in use. Disabling only hides it from pickers — open work keeps the skill and keeps billing.',
        `Open tasks: ${row.openTasks}`,
        `Open projects: ${row.openProjects}`,
        `Open quotes: ${row.openQuotes}`,
        `Templates: ${row.templates}`,
    ];
    if (row.accountRates > 0) {
        lines.push(`${plural(row.accountRates, 'special rate row')} stay in place and keep pricing finished work.`);
    }
    return lines.join('\n');
};

const AdminSkills = () => {
    const dispatch = useDispatch();
    const skills = useSelector((state) => state.skills || []);
    const [filteredSkills, setFilteredSkills] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [skillToDelete, setSkillToDelete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [usage, setUsage] = useState(null);

    useEffect(() => {
        loadSkills();
        dispatch(getSkillUsage({ callback: (rows) => rows && setUsage(Object.fromEntries(rows.map((row) => [row.skill, row]))) }));
    }, []);

    useEffect(() => {
        filterSkills();
    }, [searchTerm, skills]);

    useEffect(() => {
        if (skills.length > 0) {
            setLoading(false);
        }
    }, [skills]);

    const loadSkills = () => {
        setLoading(true);
        dispatch(getSkills());
    };

    const filterSkills = () => {
        if (!searchTerm.trim()) {
            setFilteredSkills(skills);
        } else {
            const term = searchTerm.toLowerCase();
            setFilteredSkills(
                skills.filter((s) => s.name?.toLowerCase().includes(term))
            );
        }
    };

    const handleAddClick = () => {
        setDialogMode('create');
        setSelectedSkill(null);
        setDialogOpen(true);
    };

    const handleEditClick = (skill) => {
        setDialogMode('edit');
        setSelectedSkill(skill);
        setDialogOpen(true);
    };

    const handleToggleEnabled = (skill) => {
        dispatch(editSkill({ skillId: skill._id, skillData: { enabled: skill.enabled === false } }));
    };

    const handleDeleteClick = (skill) => {
        setSkillToDelete(skill);
        setConfirmDialogOpen(true);
    };

    const handleSave = (formData) => {
        if (dialogMode === 'create') {
            dispatch(createSkill({ skillData: formData }));
        } else {
            dispatch(editSkill({ skillId: selectedSkill._id, skillData: formData }));
        }
    };

    const renderUsage = (skill) => {
        if (!usage) {
            return <UsageCounts variant="body2">—</UsageCounts>;
        }
        const row = usage[skill._id] || EMPTY_USAGE;
        return (
            <Tooltip title={<TooltipLines>{usageTooltip(row)}</TooltipLines>}>
                <UsageStack>
                    <Chip
                        label={row.safeToDisable ? 'Safe to disable' : 'In use'}
                        size="small"
                        color={row.safeToDisable ? 'success' : 'warning'}
                        variant={row.safeToDisable ? 'filled' : 'outlined'}
                    />
                    <UsageCounts variant="body2">{usageSummary(row)}</UsageCounts>
                </UsageStack>
            </Tooltip>
        );
    };

    const handleConfirmDelete = () => {
        dispatch(removeSkill({ skillId: skillToDelete._id }));
        setConfirmDialogOpen(false);
        setSkillToDelete(null);
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Skills Management"
                subtitle="Manage system skills with billing rates"
                actions={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        Add Skill
                    </Button>
                }
            />

            <SearchRow>
                <TextField
                    fullWidth
                    placeholder="Search skills by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </SearchRow>

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : filteredSkills.length === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        {searchTerm ? 'No skills found matching your search.' : 'No skills created yet. Click "Add Skill" to create one.'}
                    </Typography>
                </EmptyState>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Skill Name</strong></TableCell>
                                <TableCell align="right"><strong>Hourly Rate</strong></TableCell>
                                <TableCell align="right"><strong>Retainer Rate</strong></TableCell>
                                <TableCell align="center"><strong>Account Rates</strong></TableCell>
                                <TableCell align="center"><strong>Usage</strong></TableCell>
                                <TableCell align="center"><strong>Enabled</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredSkills.map((skill) => (
                                <SkillRow key={skill._id} hover muted={skill.enabled === false}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {skill.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={`$${skill.hourlyRate?.toFixed(2) || '0.00'}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Chip
                                            label={`$${skill.retainerRate?.toFixed(2) || '0.00'}`}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={skill.accountRates?.length || 0}
                                            size="small"
                                            color={skill.accountRates?.length > 0 ? 'primary' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <StatusCell align="center">
                                        {renderUsage(skill)}
                                    </StatusCell>
                                    <StatusCell align="center">
                                        <Tooltip title={skill.enabled === false
                                            ? 'Disabled — hidden from skill pickers. Existing work keeps this skill and keeps billing.'
                                            : 'Enabled — offered when a skill is picked for new work.'}>
                                            <Switch
                                                size="small"
                                                checked={skill.enabled !== false}
                                                onChange={() => handleToggleEnabled(skill)}
                                                inputProps={{ 'aria-label': `Enable ${skill.name}` }}
                                            />
                                        </Tooltip>
                                    </StatusCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleEditClick(skill)} color="primary">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteClick(skill)} color="error">
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </SkillRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <SkillDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                skill={selectedSkill}
                mode={dialogMode}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={() => {
                    setConfirmDialogOpen(false);
                    setSkillToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                message={`Are you sure you want to delete the skill "${skillToDelete?.name}"? This action cannot be undone.`}
            />
        </AdminPageContainer>
    );
};

export default AdminSkills;
